/**
 * Allys Property List View
 * 
 * Complete property listing page with advanced search, filtering, sorting, and view switching
 * Theme-specific implementation for Allys Real Estate
 */

'use client';

import type { SortOption } from '@/components/filters/SortControl';
import type { ViewMode } from '@/components/properties/ViewSwitcher';
import { Pagination } from '@/components/ui/Pagination';
import { Faders } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import type { Property } from '../../../../src/lib/api/clients/properties';
import { AllysAdvancedFiltersModal, type AdvancedFilterValues, type FilterSection } from './AllysAdvancedFiltersModal';
import { AllysBasicFilters, type BasicFilterValues } from './AllysBasicFilters';
import { AllysPropertyListCard } from './AllysPropertyListCard';
import { AllysPropertyMapView } from './AllysPropertyMapView';
import { AllysSearchBar } from './AllysSearchBar';
import { AllysSortControl } from './AllysSortControl';
import { AllysViewSwitcher } from './AllysViewSwitcher';
import { AllysPropertyGrid } from './PropertyGrid';
import { extractBestPriceSimple } from '@/lib/utils/price';

export interface AllysPropertyListViewProps {
  /**
   * Properties to display
   */
  properties: Property[];
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
  /**
   * Initial view mode
   */
  initialView?: ViewMode;
  /**
   * Show map view option
   */
  showMapView?: boolean;
  /**
   * Base path for property links (e.g., '/site/org-123' for tenant sites)
   * If not provided, defaults to '/{locale}'
   */
  basePath?: string;
  /**
   * Pre-selected property type ID to filter by (resolved from URL slug)
   */
  initialPropertyTypeId?: string | null;
}

// Helper to safely get translations
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * AllysPropertyListView - Complete property search and listing interface
 */
export function AllysPropertyListView({
  properties,
  messages,
  locale = 'en',
  initialView = 'grid',
  showMapView: _showMapView = false,
  basePath,
  initialPropertyTypeId = null,
}: AllysPropertyListViewProps) {
  // Default basePath to /{locale} if not provided
  const effectiveBasePath = basePath || `/${locale}`;
  // Get translations
  const propListMessages = messages?.propertiesList as Record<string, unknown> | undefined;
  const searchMessages = propListMessages?.search as Record<string, unknown> | undefined;
  const sortMessages = propListMessages?.sort as Record<string, unknown> | undefined;
  const sortOptionsMsg = sortMessages?.options as Record<string, unknown> | undefined;
  const filtersMessages = propListMessages?.filters as Record<string, unknown> | undefined;
  const filtersGroups = filtersMessages?.groups as Record<string, unknown> | undefined;
  const viewsMessages = propListMessages?.views as Record<string, unknown> | undefined;
  const cardMessages = propListMessages?.card as Record<string, unknown> | undefined;
  const emptyMessages = propListMessages?.empty as Record<string, unknown> | undefined;
  
  // State
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('priceAsc');
  const [basicFilterValues, setBasicFilterValues] = useState<BasicFilterValues>({});
  const [advancedFilterValues, setAdvancedFilterValues] = useState<AdvancedFilterValues>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [compareList, setCompareList] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // Properties per page
  
  // Merge basic and advanced filters
  const filterValues = useMemo(() => {
    return {
      ...advancedFilterValues,
      ...basicFilterValues,
    } as Record<string, string | string[] | number | { min?: number; max?: number }>;
  }, [basicFilterValues, advancedFilterValues]);
  
  // Sort options
  const sortOptions: SortOption<Property>[] = [
    {
      label: safeGetString(sortOptionsMsg?.priceAsc, 'Price: Low to High'),
      value: 'priceAsc',
      orderBy: (item) => extractBestPriceSimple(item).amount || 0,
      direction: 'asc',
    },
    {
      label: safeGetString(sortOptionsMsg?.priceDesc, 'Price: High to Low'),
      value: 'priceDesc',
      orderBy: (item) => extractBestPriceSimple(item).amount || 0,
      direction: 'desc',
    },
    {
      label: safeGetString(sortOptionsMsg?.bedroomsDesc, 'Most Bedrooms'),
      value: 'bedroomsDesc',
      orderBy: (item) => item.bedrooms || 0,
      direction: 'desc',
    },
    {
      label: safeGetString(sortOptionsMsg?.bedroomsAsc, 'Fewest Bedrooms'),
      value: 'bedroomsAsc',
      orderBy: (item) => item.bedrooms || 0,
      direction: 'asc',
    },
    {
      label: safeGetString(sortOptionsMsg?.areaDesc, 'Largest Area'),
      value: 'areaDesc',
      orderBy: (item) => item.totalFloorArea || 0,
      direction: 'desc',
    },
    {
      label: safeGetString(sortOptionsMsg?.areaAsc, 'Smallest Area'),
      value: 'areaAsc',
      orderBy: (item) => item.totalFloorArea || 0,
      direction: 'asc',
    },
    {
      label: safeGetString(sortOptionsMsg?.newest, 'Newest First'),
      value: 'newest',
      orderBy: (item) => new Date(item.createdAt).getTime(),
      direction: 'desc',
    },
  ];
  
  // Filter sections
  const bedroomsGroup = filtersGroups?.bedrooms as Record<string, unknown> | undefined;
  const bedroomsOptions = bedroomsGroup?.options as Record<string, unknown> | undefined;
  const bathroomsGroup = filtersGroups?.bathrooms as Record<string, unknown> | undefined;
  const bathroomsOptions = bathroomsGroup?.options as Record<string, unknown> | undefined;
  const priceRangeGroup = filtersGroups?.priceRange as Record<string, unknown> | undefined;
  const areaGroup = filtersGroups?.area as Record<string, unknown> | undefined;
  const amenitiesGroup = filtersGroups?.amenities as Record<string, unknown> | undefined;
  const amenitiesOptions = amenitiesGroup?.options as Record<string, unknown> | undefined;
  const propertyFeaturesGroup = filtersGroups?.propertyFeatures as Record<string, unknown> | undefined;
  const propertyFeaturesOptions = propertyFeaturesGroup?.options as Record<string, unknown> | undefined;
  const foreignerEligibilityGroup = filtersGroups?.foreignerEligibility as Record<string, unknown> | undefined;
  const foreignerEligibilityOptions = foreignerEligibilityGroup?.options as Record<string, unknown> | undefined;
  const investmentSchemeGroup = filtersGroups?.investmentScheme as Record<string, unknown> | undefined;
  const investmentSchemeOptions = investmentSchemeGroup?.options as Record<string, unknown> | undefined;
  const developmentGroup = filtersGroups?.development as Record<string, unknown> | undefined;
  const developmentOptions = developmentGroup?.options as Record<string, unknown> | undefined;
  
  const filterSections: FilterSection[] = [
    {
      id: 'development',
      label: safeGetString(developmentGroup?.label, 'Development / Project'),
      type: 'checkbox',
      defaultExpanded: true,
      options: [
        { value: 'azuri', label: safeGetString(developmentOptions?.azuri, 'Azuri Ocean & Golf Village') },
        { value: 'anahita', label: safeGetString(developmentOptions?.anahita, 'Anahita The Resort') },
        { value: 'tamarina', label: safeGetString(developmentOptions?.tamarina, 'Tamarina Golf Estate') },
        { value: 'laCite', label: safeGetString(developmentOptions?.laCite, 'La Cité Business Park') },
        { value: 'moka', label: safeGetString(developmentOptions?.moka, 'Moka Smart City') },
        { value: 'highlands', label: safeGetString(developmentOptions?.highlands, 'The Highlands') },
        { value: 'savannah', label: safeGetString(developmentOptions?.savannah, 'Savannah Estate') },
        { value: 'lesMartinique', label: safeGetString(developmentOptions?.lesMartinique, 'Les Martinique') },
        { value: 'independent', label: safeGetString(developmentOptions?.independent, 'Independent Properties') },
      ],
    },
    {
      id: 'priceRange',
      label: safeGetString(priceRangeGroup?.label, 'Price Range'),
      type: 'price-range',
      min: 0,
      max: 10000000,
      step: 50000,
      defaultExpanded: true,
    },
    {
      id: 'bedrooms',
      label: safeGetString(bedroomsGroup?.label, 'Bedrooms'),
      type: 'checkbox',
      defaultExpanded: true,
      options: [
        { value: '1', label: safeGetString(bedroomsOptions?.['1'], '1 Bedroom') },
        { value: '2', label: safeGetString(bedroomsOptions?.['2'], '2 Bedrooms') },
        { value: '3', label: safeGetString(bedroomsOptions?.['3'], '3 Bedrooms') },
        { value: '4', label: safeGetString(bedroomsOptions?.['4'], '4 Bedrooms') },
        { value: '5+', label: safeGetString(bedroomsOptions?.['5+'], '5+ Bedrooms') },
      ],
    },
    {
      id: 'bathrooms',
      label: safeGetString(bathroomsGroup?.label, 'Bathrooms'),
      type: 'checkbox',
      defaultExpanded: true,
      options: [
        { value: '1', label: safeGetString(bathroomsOptions?.['1'], '1 Bathroom') },
        { value: '2', label: safeGetString(bathroomsOptions?.['2'], '2 Bathrooms') },
        { value: '3', label: safeGetString(bathroomsOptions?.['3'], '3 Bathrooms') },
        { value: '4+', label: safeGetString(bathroomsOptions?.['4+'], '4+ Bathrooms') },
      ],
    },
    {
      id: 'area',
      label: safeGetString(areaGroup?.label, 'Floor Area (sqft)'),
      type: 'range',
      min: 0,
      max: 10000,
      step: 100,
      defaultExpanded: false,
    },
    {
      id: 'amenities',
      label: safeGetString(amenitiesGroup?.label, 'Amenities'),
      type: 'checkbox',
      defaultExpanded: false,
      options: [
        { value: 'parking', label: safeGetString(amenitiesOptions?.parking, 'Parking') },
        { value: 'garage', label: safeGetString(amenitiesOptions?.garage, 'Garage') },
        { value: 'pool', label: safeGetString(amenitiesOptions?.pool, 'Swimming Pool') },
        { value: 'garden', label: safeGetString(amenitiesOptions?.garden, 'Garden') },
        { value: 'balcony', label: safeGetString(amenitiesOptions?.balcony, 'Balcony') },
        { value: 'gym', label: safeGetString(amenitiesOptions?.gym, 'Gym') },
      ],
    },
    {
      id: 'propertyFeatures',
      label: safeGetString(propertyFeaturesGroup?.label, 'Property Features'),
      type: 'checkbox',
      defaultExpanded: true,
      options: [
        { value: 'beachfront', label: safeGetString(propertyFeaturesOptions?.beachfront, 'Beachfront') },
        { value: 'smartCity', label: safeGetString(propertyFeaturesOptions?.smartCity, 'Smart City') },
        { value: 'farm', label: safeGetString(propertyFeaturesOptions?.farm, 'Farm / Agricultural') },
        { value: 'golfCourse', label: safeGetString(propertyFeaturesOptions?.golfCourse, 'Golf Course') },
        { value: 'waterfront', label: safeGetString(propertyFeaturesOptions?.waterfront, 'Waterfront') },
        { value: 'mountain', label: safeGetString(propertyFeaturesOptions?.mountain, 'Mountain View') },
        { value: 'cityCenter', label: safeGetString(propertyFeaturesOptions?.cityCenter, 'City Center') },
        { value: 'gatedCommunity', label: safeGetString(propertyFeaturesOptions?.gatedCommunity, 'Gated Community') },
        { value: 'newDevelopment', label: safeGetString(propertyFeaturesOptions?.newDevelopment, 'New Development') },
        { value: 'luxury', label: safeGetString(propertyFeaturesOptions?.luxury, 'Luxury Property') },
      ],
    },
    {
      id: 'foreignerEligibility',
      label: safeGetString(foreignerEligibilityGroup?.label, 'Available for Foreigners'),
      type: 'radio',
      defaultExpanded: false,
      options: [
        { value: 'yes', label: safeGetString(foreignerEligibilityOptions?.yes, 'Yes') },
        { value: 'no', label: safeGetString(foreignerEligibilityOptions?.no, 'No') },
      ],
    },
    {
      id: 'investmentScheme',
      label: safeGetString(investmentSchemeGroup?.label, 'Investment Schemes'),
      type: 'checkbox',
      defaultExpanded: false,
      options: [
        { value: 'irs', label: safeGetString(investmentSchemeOptions?.irs, 'IRS (Integrated Resort Scheme)') },
        { value: 'smartCity', label: safeGetString(investmentSchemeOptions?.smartCity, 'Smart City Scheme') },
        { value: 'pds', label: safeGetString(investmentSchemeOptions?.pds, 'PDS (Property Development Scheme)') },
        { value: 'res', label: safeGetString(investmentSchemeOptions?.res, 'RES (Real Estate Scheme)') },
        { value: 'villas', label: safeGetString(investmentSchemeOptions?.villas, 'Villas Scheme') },
        { value: 'none', label: safeGetString(investmentSchemeOptions?.none, 'No Special Scheme') },
      ],
    },
  ];
  
  // Advanced search function that searches across multiple fields
  const searchFn = useCallback((property: Property, query: string): boolean => {
    const lowerQuery = query.toLowerCase();
    return !!(
      property.name.toLowerCase().includes(lowerQuery) ||
      property.description?.toLowerCase().includes(lowerQuery) ||
      property.reference?.toLowerCase().includes(lowerQuery)
    );
  }, []);
  
  // Comprehensive filter function that uses current filterValues
  const filterFn = useCallback((property: Property): boolean => {
    // Price Range Filter
    if (filterValues.priceRange) {
      const range = filterValues.priceRange as { min?: number; max?: number };
      const price = extractBestPriceSimple(property).amount || 0;
      if (range.min && price < range.min) return false;
      if (range.max && price > range.max) return false;
    }
    
    // Bedrooms Filter
    if (filterValues.bedrooms) {
      const bedrooms = Array.isArray(filterValues.bedrooms) ? filterValues.bedrooms : [filterValues.bedrooms];
      const propBedrooms = property.bedrooms || 0;
      const matches = bedrooms.some(b => {
        if (b === '5+') return propBedrooms >= 5;
        return propBedrooms === parseInt(b as string);
      });
      if (!matches) return false;
    }
    
    // Bathrooms Filter
    if (filterValues.bathrooms) {
      const bathrooms = Array.isArray(filterValues.bathrooms) ? filterValues.bathrooms : [filterValues.bathrooms];
      const propBathrooms = property.bathrooms || 0;
      const matches = bathrooms.some(b => {
        if (b === '4+') return propBathrooms >= 4;
        return propBathrooms === parseInt(b as string);
      });
      if (!matches) return false;
    }
    
    // Area Filter
    if (filterValues.area) {
      const range = filterValues.area as { min?: number; max?: number };
      const area = property.totalFloorArea || 0;
      if (range.min && area < range.min) return false;
      if (range.max && area > range.max) return false;
    }

    // Property Type Filter
    if (initialPropertyTypeId && property.propertyType?.id !== initialPropertyTypeId) {
      return false;
    }
    
    return true;
  }, [filterValues, initialPropertyTypeId]);
  
  // Manual filtering and sorting (hook signature doesn't match our needs exactly)
  const filteredAndSortedProperties = useMemo(() => {
    let result = [...properties];
    
    // Apply search
    if (searchValue) {
      result = result.filter(item => searchFn(item, searchValue));
    }
    
    // Apply filters
    result = result.filter(filterFn);
    
    // Apply sort
    const selectedSort = sortOptions.find(opt => opt.value === sortValue);
    if (selectedSort) {
      result.sort((a, b) => {
        const aValue = selectedSort.orderBy(a);
        const bValue = selectedSort.orderBy(b);
        
        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        const comparison = aValue < bValue ? -1 : 1;
        return selectedSort.direction === 'desc' ? -comparison : comparison;
      });
    }
    
    return result;
  }, [properties, searchValue, filterFn, searchFn, sortValue, sortOptions]);
  
  // Pagination
  const totalPages = Math.ceil(filteredAndSortedProperties.length / itemsPerPage);
  const paginatedProperties = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedProperties.slice(startIndex, endIndex);
  }, [filteredAndSortedProperties, currentPage, itemsPerPage]);
  
  // Reset to page 1 when filters change
  const handleBasicFilterChange = (newValues: BasicFilterValues) => {
    setBasicFilterValues(newValues);
    setCurrentPage(1);
  };
  
  const handleAdvancedFilterChange = (newValues: AdvancedFilterValues) => {
    setAdvancedFilterValues(newValues);
    setCurrentPage(1);
  };
  
  const handleSearchChange = (query: string) => {
    setSearchValue(query);
    setCurrentPage(1);
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Advanced Filters Modal */}
      <AllysAdvancedFiltersModal
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        values={advancedFilterValues}
        onApply={handleAdvancedFilterChange}
        filterSections={filterSections}
        labels={{
          title: safeGetString(filtersMessages?.advanced, 'Advanced Filters'),
          clearAll: safeGetString(filtersMessages?.clear, 'Clear all'),
          cancel: safeGetString(filtersMessages?.cancel, 'Cancel'),
          apply: safeGetString(filtersMessages?.apply, 'Apply Filters'),
        }}
        resultsCount={filteredAndSortedProperties.length}
      />
      
      {/* Main Content - Starts at top */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 pt-20 sm:pt-24 lg:pt-28 pb-12">
        <div className="flex flex-col gap-8">
          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Search & Filter Toolbar */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-white via-white to-gray-50 rounded-2xl shadow-xl border-2 border-gray-200 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 relative"
            >
              {/* Decorative gradient background */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#243462] via-[#BBA339] to-[#E4CE4A] rounded-t-2xl" />
              
              {/* Subtle background pattern */}
              <div className="absolute inset-0 opacity-[0.02] pointer-events-none rounded-2xl" style={{ 
                backgroundImage: 'radial-gradient(circle at 1px 1px, #243462 1px, transparent 0)',
                backgroundSize: '24px 24px'
              }} />
              
              <div className="relative space-y-4 sm:space-y-6">
                {/* First Row: Search Bar and View Switcher */}
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search Bar */}
                  <div className="flex-1">
                    <AllysSearchBar
                      placeholder={safeGetString(searchMessages?.placeholder, 'Search by location, property name, or reference...')}
                      value={searchValue}
                      onChange={handleSearchChange}
                    />
                  </div>
                  
                  {/* View Switcher */}
                  <AllysViewSwitcher
                    activeView={viewMode}
                    onViewChange={setViewMode}
                    labels={{
                      grid: safeGetString(viewsMessages?.grid, 'Grid'),
                      list: safeGetString(viewsMessages?.list, 'List'),
                      map: safeGetString(viewsMessages?.map, 'Map'),
                    }}
                  />
                </div>
                
                {/* Second Row: Basic Filters */}
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <AllysBasicFilters
                    values={basicFilterValues}
                    onChange={handleBasicFilterChange}
                    labels={{
                      bedrooms: safeGetString(bedroomsGroup?.label, 'Bedrooms'),
                      bathrooms: safeGetString(bathroomsGroup?.label, 'Bathrooms'),
                      priceRange: safeGetString(priceRangeGroup?.label, 'Price Range'),
                      foreignerEligible: safeGetString(foreignerEligibilityGroup?.label, 'Available to Foreigners'),
                      anyBedrooms: safeGetString(filtersMessages?.any, 'Any'),
                      anyBathrooms: safeGetString(filtersMessages?.any, 'Any'),
                      min: safeGetString(filtersMessages?.min, 'Min'),
                      max: safeGetString(filtersMessages?.max, 'Max'),
                      apply: safeGetString(filtersMessages?.apply, 'Apply'),
                      clear: safeGetString(filtersMessages?.clear, 'Clear all'),
                      selected: safeGetString(filtersMessages?.selected, 'Selected'),
                      bedroom: safeGetString(filtersMessages?.bedroom, 'Bedroom'),
                      bathroom: safeGetString(filtersMessages?.bathroom, 'Bathroom'),
                    }}
                    className="flex-1"
                  />
                  
                  {/* Advanced Filters Button */}
                <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setShowAdvancedFilters(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] hover:from-[#a89333] hover:to-[#d4be45] text-white font-bold rounded-xl transition-all duration-300 shadow-md hover:shadow-lg border-2 border-[#BBA339] whitespace-nowrap font-inter"
                >
                    <Faders size={20} weight="bold" />
                    {safeGetString(filtersMessages?.advanced, 'Advanced Filters')}
                </motion.button>
                </div>
                
                {/* Third Row: Sort and Results Count */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2 border-t border-gray-200">
                {/* Sort Control */}
                <div className="flex-1">
                  <AllysSortControl
                    options={sortOptions}
                    value={sortValue}
                    onChange={setSortValue}
                    label={safeGetString(sortMessages?.label, 'Sort by')}
                  />
                </div>
                
                {/* Results Count - Enhanced */}
                <motion.div 
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#243462]/5 to-[#BBA339]/5 rounded-xl border border-gray-100"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#BBA339] to-[#E4CE4A] flex items-center justify-center shadow-sm">
                    <span className="text-white font-bold text-xs font-inter">
                      {filteredAndSortedProperties.length}
                    </span>
                  </div>
                  <span className="text-sm text-gray-700 font-semibold whitespace-nowrap font-inter">
                    {filteredAndSortedProperties.length === 1 
                      ? safeGetString(propListMessages?.property, 'property') 
                      : safeGetString(propListMessages?.properties, 'properties')
                    }
                  </span>
                </motion.div>
              </div>
              </div>
            </motion.div>
            
            {/* Properties Display */}
            <AnimatePresence mode="wait">
              {filteredAndSortedProperties.length > 0 ? (
                <motion.div
                  key={viewMode}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {viewMode === 'grid' && (
                    <>
                      <AllysPropertyGrid
                        properties={paginatedProperties}
                        columns={3}
                        gap="xl"
                        title=""
                        subtitle=""
                        {...(messages && { messages })}
                        {...(locale && { locale })}
                        basePath={effectiveBasePath}
                      />
                      
                      {/* Pagination for Grid */}
                      {totalPages > 1 && (
                        <div className="mt-12">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            labels={{
                              previous: 'Previous',
                              next: 'Next',
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                  
                  {viewMode === 'list' && (
                    <>
                      <div className="space-y-6">
                        {paginatedProperties.map((property, index) => {
                          // Map property locations (mock data)
                          const locationMap: Record<string, string> = {
                            '1': 'Grand Baie',
                            '2': 'Black River',
                            '3': 'Tamarin',
                            '4': 'Belle Mare',
                            '5': 'Flic en Flac',
                            '6': 'Bel Ombre',
                            '7': 'Trou aux Biches',
                            '8': 'Port Louis',
                            '9': 'Moka',
                          };
                          
                          return (
                            <motion.div
                              key={property.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05, duration: 0.4 }}
                            >
                              <AllysPropertyListCard
                                property={property}
                                locationName={locationMap[property.id] || 'Mauritius'}
                                labels={{
                                  bedrooms: safeGetString(cardMessages?.bedrooms, 'Beds'),
                                  bathrooms: safeGetString(cardMessages?.bathrooms, 'Baths'),
                                  area: safeGetString(cardMessages?.area, 'Area'),
                                  viewDetails: safeGetString(cardMessages?.viewDetails, 'View Details'),
                                  contactAgent: safeGetString(cardMessages?.contactAgent, 'Contact Agent'),
                                  save: safeGetString(cardMessages?.save, 'Save'),
                                  saved: safeGetString(cardMessages?.saved, 'Saved'),
                                  share: safeGetString(cardMessages?.share, 'Share'),
                                  askingPrice: safeGetString(cardMessages?.askingPrice, 'Asking Price'),
                                  built: safeGetString(cardMessages?.built, 'Built'),
                                  renovated: safeGetString(cardMessages?.renovated, 'Renovated'),
                                  parking: safeGetString(cardMessages?.parking, 'Parking'),
                                  sold: safeGetString(cardMessages?.sold, 'Sold'),
                                  reserved: safeGetString(cardMessages?.reserved, 'Reserved'),
                                  contactForPrice: safeGetString(cardMessages?.contactForPrice, 'Contact for price'),
                                }}
                                href={`${effectiveBasePath}/properties/${property.id}`}
                              />
                            </motion.div>
                          );
                        })}
                      </div>
                      
                      {/* Pagination for List */}
                      {totalPages > 1 && (
                        <div className="mt-12">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            labels={{
                              previous: 'Previous',
                              next: 'Next',
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                  
                  {viewMode === 'map' && (
                    <AllysPropertyMapView
                      properties={filteredAndSortedProperties}
                      organisationId="allys"
                      locale={locale}
                      labels={{
                        bedrooms: safeGetString(cardMessages?.bedrooms, 'Beds'),
                        bathrooms: safeGetString(cardMessages?.bathrooms, 'Baths'),
                        area: safeGetString(cardMessages?.area, 'Area'),
                        viewDetails: safeGetString(cardMessages?.viewDetails, 'View'),
                      }}
                      mapPosition="right"
                      height="calc(100vh - 320px)"
                      emptyMessage={safeGetString(emptyMessages?.title, 'No properties found')}
                      basePath={effectiveBasePath}
                    />
                  )}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-16 text-center"
                >
                  <svg className="w-24 h-24 mx-auto mb-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4 font-inter">
                    {safeGetString(emptyMessages?.title, 'No properties found')}
                  </h3>
                  <p className="text-gray-600 text-lg mb-8 max-w-lg mx-auto font-inter">
                    {safeGetString(emptyMessages?.description, 'Try adjusting your filters or search criteria')}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSearchValue('');
                      setBasicFilterValues({});
                      setAdvancedFilterValues({});
                      setCurrentPage(1);
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-[#243462] to-[#121C3B] hover:from-[#2a3d74] hover:to-[#1a2649] text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-inter"
                  >
                    {safeGetString(emptyMessages?.resetButton, 'Reset Filters')}
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      
      {/* Compare Bar (Fixed at bottom when properties selected) */}
      <AnimatePresence>
        {compareList.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 backdrop-blur-xl"
          >
            <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <span className="font-bold text-gray-900 text-lg font-inter">
                    Compare ({compareList.length}/4)
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCompareList([])}
                    className="text-sm text-red-600 hover:text-red-700 font-semibold px-4 py-2 rounded-lg hover:bg-red-50 transition-all font-inter"
                  >
                    Clear All
                  </motion.button>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3.5 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] hover:from-[#a89333] hover:to-[#d4be45] text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-inter"
                >
                  Compare Properties
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

